import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import type { ChapterEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { CreateMtrDialog } from 'pages/Project/UerProject/WorkOrder/CreateMtrDialog';
import { Button, cnMixSpace, HeaderSearchBar, IconClose } from 'uikit';

import type { GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAddItemsToWorkOrderChange, useAddMtrToWorkOrderTableData } from '../hooks';
import { addMtrToWorkOrderChange } from '../WorkOrderChangeQueries';

export const AddMtrToWorkOrderChange = ({
    isOpened,
    closeModalHandler,
    checkedRow,
    resetCheckedRows,
    refreshTable,
    chapter,
    workOrderChangeUerRules,
}: {
    isOpened: boolean;
    closeModalHandler: () => void;
    checkedRow: {
        workOrderChangePriceId: number | null;
        workOrderPriceId: number;
        workOrderObjectId: number | undefined | null;
    }[];
    resetCheckedRows: () => void;
    refreshTable: (objectIds: number[]) => void;
    chapter: ChapterEnum | undefined | null;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
}) => {
    const location = useLocation();
    const state = location?.state as { selected: number; reload: number } | undefined;
    const navigate = useNavigate();
    const tableRef = useRef<AgGridReact>(null);

    useEffect(
        () => () => {
            navigate(location.pathname, { replace: true });
        },
        [location.pathname, navigate],
    );

    const { workOrderChangePriceId, workOrderPriceId } = useMemo(
        () => ({
            workOrderChangePriceId: checkedRow?.[0]?.workOrderChangePriceId,
            workOrderPriceId: checkedRow?.[0]?.workOrderPriceId,
        }),
        [checkedRow],
    );

    const {
        columns,
        checkedMtrArr,
        searchString,
        changeSearchStringHandler,
        searchHandler,
        clearSearchStringHandler,
        mtrInputs,
        datasource,
        clearFilters,
        debouncedSearchValue,
    } = useAddMtrToWorkOrderTableData({
        workOrderChangePriceId,
        workOrderPriceId,
        locationState: state,
    });

    const objectIdsArrEditMutation = useMemo(
        () => (checkedRow?.[0]?.workOrderObjectId ? [checkedRow?.[0]?.workOrderObjectId] : []),
        [checkedRow],
    );

    const onSuccessAddHandler = useCallback(() => {
        refreshTable(objectIdsArrEditMutation);
        resetCheckedRows();
        closeModalHandler();
        clearFilters();
    }, [clearFilters, closeModalHandler, objectIdsArrEditMutation, refreshTable, resetCheckedRows]);

    const onCloseHandler = useCallback(() => {
        clearFilters();
        closeModalHandler();
    }, [clearFilters, closeModalHandler]);

    const variables = useMemo(
        () => ({
            workOrderChangePriceId,
            workOrderPriceId,
            mtrInputs,
        }),
        [mtrInputs, workOrderChangePriceId, workOrderPriceId],
    );

    const {
        addToWorkOrderChangeHandler,
        addResponse,
        addStatus,
        addErr,
        addIsSubmitted,
        closeErrorModal,
        handleCreateReport,
    } = useAddItemsToWorkOrderChange({
        query: addMtrToWorkOrderChange,
        requestId: 'uerAddMtrToWorkOrderChange',
        variables,
        onSuccessAddHandler,
    });

    const isConfirmBtnDisabled = !checkedMtrArr.length;

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    const refreshMtrPriceResponse = useCallback(() => {
        tableRef?.current?.api?.refreshInfiniteCache();
    }, []);

    return (
        <ModalDialog
            title="Добавление МТР из прейскуранта МТР"
            isOpen={isOpened}
            hasOverlay
            withTitleCloseButton
            onClose={onCloseHandler}
            onConfirm={addToWorkOrderChangeHandler}
            btnConfirmDisabled={isConfirmBtnDisabled}
            btnConfirmLabel="Добавить в Изм. к НЗ"
            btnCancelLabel="Отменить"
            leftPanel={
                <CreateMtrDialog
                    refreshMtrPriceResponse={refreshMtrPriceResponse}
                    chapter={chapter}
                    accessRules={workOrderChangeUerRules}
                />
            }
            style={{ overflow: 'hidden', maxWidth: '1300px', width: '100%' }}
            isOnClickOutsideDisable
        >
            <div id="addMtrDialog">
                <div className={cnMixSpace({ mT: 'xl', mB: 'm' })} style={{ position: 'relative' }}>
                    <HeaderSearchBar
                        className="AddMtrSearch"
                        placeholder="Поиск МТР в прейскуранте"
                        label="поиск"
                        value={searchString}
                        onChange={changeSearchStringHandler}
                        onSearch={searchHandler}
                    />
                    <Button
                        label="Очистить"
                        view="ghost"
                        iconLeft={IconClose}
                        form="round"
                        size="xs"
                        onlyIcon
                        onClick={clearSearchStringHandler}
                        className="clearSearchStringBtn"
                    />
                </div>
                <div style={{ height: '500px' }} className="ag-theme-alpine gridWrapper">
                    <AgGridTable
                        key={debouncedSearchValue}
                        ref={tableRef}
                        getRowId={getRowId}
                        datasource={datasource}
                        columnDefs={columns}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        onGridReady={onGridReady}
                        suppressCellFocus
                    />
                </div>
                <ErrorHandlingModal
                    titleText="Добавление МТР из прейскуранта МТР"
                    isSubmitted={addIsSubmitted}
                    statusRequest={addStatus}
                    dataRequest={addResponse?.uerAddMtrToWorkOrderChange}
                    errorsRequest={addErr}
                    handleCloseModalError={closeErrorModal}
                    handleCreateReport={handleCreateReport}
                />
            </div>
        </ModalDialog>
    );
};

AddMtrToWorkOrderChange.displayName = 'AddMtrToWorkOrderChange';
